/**
 * MediaBrowser - Archive panel tab for browsing media items
 *
 * Fetches media items from the API and displays them in a grid/list view.
 * Supports filtering by type, searching by caption/description, and pagination.
 *
 * @module @humanizer/studio/components/archive/MediaBrowser
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMediaApi, type MediaItem } from '../../hooks/useMediaApi';
import { useMedia, useMediaSelection, type MediaItem as ContextMediaItem } from '../../contexts/MediaContext';
import { usePanels } from '../../contexts/PanelContext';
import type { ApiMediaItem } from '../../types/media';
import { resolveMediaUrl } from '../../utils/media-url';
import { SourceFilterPills, type FilterState } from '../ui/SourceFilterPills';
import { TranscriptionPanel } from '../media/TranscriptionPanel';
import { LOCAL_ARCHIVE_UUID } from '@humanizer/core/aui';
import { createLogger } from '../../utils/debug';

const log = createLogger('MediaBrowser');

type MediaTypeFilter = 'all' | 'image' | 'audio' | 'video';
type ViewMode = 'grid' | 'list';

/** Extract a short, readable file ID from a source pointer */
function extractFileId(pointer: string | null | undefined): string {
  if (!pointer) return '';
  // file-service://file-Xnnt6V4p... → file-Xnnt6V4p...
  const stripped = pointer.replace(/^file-service:\/\//, '').replace(/^sediment:\/\//, '');
  // Truncate long IDs for display
  return stripped.length > 20 ? stripped.slice(0, 20) + '…' : stripped;
}

/** Format a date for display, preferring source date over import date */
function formatMediaDate(item: ApiMediaItem): string {
  const dateStr = item.sourceCreatedAt || item.createdAt;
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** Map an API media item to the MediaContext MediaItem shape */
function apiItemToContextItem(item: ApiMediaItem): ContextMediaItem {
  const mimeType = item.mimeType || 'application/octet-stream';
  let type: 'image' | 'audio' | 'video' = 'image';
  if (mimeType.startsWith('audio/')) type = 'audio';
  else if (mimeType.startsWith('video/')) type = 'video';

  return {
    id: item.id,
    archiveId: LOCAL_ARCHIVE_UUID,
    type,
    filename: item.filename || '',
    mimeType,
    size: item.size || 0,
    dimensions: item.width && item.height ? { width: item.width, height: item.height } : undefined,
    sourceUrl: item.sourcePointer
      ? resolveMediaUrl(item.sourcePointer)
      : `/api/media/serve/${item.id}`,
    createdAt: item.createdAt ? new Date(item.createdAt) : new Date(),
    transcriptionCount: 0,
  };
}

const STORAGE_KEY = 'humanizer:media-browser';

function loadPersistedSettings(): { viewMode: ViewMode; thumbSize: number; typeFilter: MediaTypeFilter; sourceFilter: string | null } {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return { viewMode: 'grid', thumbSize: 150, typeFilter: 'all', sourceFilter: null, ...parsed };
    }
  } catch { /* ignore */ }
  return { viewMode: 'grid', thumbSize: 150, typeFilter: 'all', sourceFilter: null };
}

function persistSettings(viewMode: ViewMode, thumbSize: number, typeFilter: MediaTypeFilter, sourceFilter: string | null): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ viewMode, thumbSize, typeFilter, sourceFilter }));
  } catch { /* ignore */ }
}

/** A conversation/content association for a media item */
interface MediaAssociation {
  nodeId: string;
  threadId: string;
  date?: string;
  authorRole?: string;
  snippet?: string;
  conversationTitle?: string;
  conversationDate?: string;
}

export interface MediaBrowserProps {
  onSelectMedia?: (item: ApiMediaItem) => void;
  onExpandGallery?: () => void;
  /** Navigate to a conversation thread by its root node ID */
  onOpenThread?: (threadId: string) => void;
  /** Open transcription text in workspace split view */
  onOpenTranscriptionInWorkspace?: (text: string, modelLabel: string) => void;
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export function MediaBrowser({ onSelectMedia, onExpandGallery, onOpenThread, onOpenTranscriptionInWorkspace: onOpenTranscriptionInWorkspaceExternal }: MediaBrowserProps): React.ReactElement {
  const mediaApi = useMediaApi();
  const { state: panelState, setPendingMediaId } = usePanels();
  const { addItem: addContextItem, deselectAll: deselectAllContext } = useMedia();
  const { selectedIds: contextSelectedIds, selectItem: contextSelectItem, deselectItem: contextDeselectItem, toggleSelection: contextToggleSelection, selectRange: contextSelectRange, deselectAll: contextDeselectAllSelection, selectedCount: contextSelectedCount } = useMediaSelection();
  const lastClickedIdRef = useRef<string | null>(null);

  const [items, setItems] = useState<ApiMediaItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const persisted = useState(() => loadPersistedSettings())[0];
  const [typeFilter, setTypeFilter] = useState<MediaTypeFilter>(persisted.typeFilter);
  const [sourceFilters, setSourceFilters] = useState<FilterState>(
    persisted.sourceFilter ? { [persisted.sourceFilter]: 'include' } : {}
  );
  // Derive single sourceFilter for API compatibility
  const sourceFilter = useMemo(() => {
    const included = Object.entries(sourceFilters).find(([, v]) => v === 'include');
    return included ? included[0] : null;
  }, [sourceFilters]);
  const [availableSources, setAvailableSources] = useState<Array<{ sourceType: string; count: number }>>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>(persisted.viewMode);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [thumbSize, setThumbSize] = useState(persisted.thumbSize);
  const [associations, setAssociations] = useState<MediaAssociation[]>([]);
  const [transcriptionRefreshKey, setTranscriptionRefreshKey] = useState(0);

  // Media cleanup state (Electron only)
  const isElectron = typeof window !== 'undefined' && !!window.isElectron;
  const [cleanupRunning, setCleanupRunning] = useState(false);
  const [cleanupResult, setCleanupResult] = useState<{
    dryRun: boolean;
    missingFiles: number;
    orphanedRefs: number;
    deletedRows: number;
  } | null>(null);

  // Persist settings on change
  useEffect(() => {
    persistSettings(viewMode, thumbSize, typeFilter, sourceFilter);
  }, [viewMode, thumbSize, typeFilter, sourceFilter]);
  const containerRef = useRef<HTMLDivElement>(null);
  const [colCount, setColCount] = useState(4);

  const PAGE_SIZE = 30;

  // Compute column count from container width and thumb size
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = entry.contentRect.width;
        setColCount(Math.max(1, Math.floor(w / thumbSize)));
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [thumbSize]);

  // Fetch available source types on mount
  useEffect(() => {
    if (!mediaApi.isAvailable) return;
    mediaApi.listSources().then(setAvailableSources).catch((err) => {
      log.warn('Failed to list sources:', err?.message ?? err);
    });
  }, [mediaApi]);

  // Fetch media items (works on both local and cloud)
  const fetchMedia = useCallback(async () => {
    if (!mediaApi.isAvailable) return;
    setLoading(true);
    setError(null);
    try {
      let result: { items: (ApiMediaItem | MediaItem)[]; total: number };

      if (searchQuery.trim()) {
        const searchResult = await mediaApi.search(searchQuery, PAGE_SIZE);
        // Map search results to items format
        result = {
          items: searchResult.results.map(r => ({
            id: r.id,
            mimeType: r.type ? `${r.type}/unknown` : 'application/octet-stream',
            filename: r.filename,
            contentHash: r.id,
            caption: r.text,
          } as ApiMediaItem)),
          total: searchResult.total,
        };
        setTotal(result.total);
      } else {
        const listResult = await mediaApi.list({
          type: typeFilter === 'all' ? undefined : typeFilter,
          sourceType: sourceFilter || undefined,
          limit: PAGE_SIZE,
          offset: page * PAGE_SIZE,
        });
        // Map list results to items format
        result = {
          items: listResult.items.map(m => ({
            id: m.id,
            mimeType: m.mimeType || m.type || 'application/octet-stream',
            filename: m.filename,
            contentHash: m.contentHash || m.id,
            sourcePointer: m.sourcePointer,
            sourceType: m.sourceType ?? null,
            sourceCreatedAt: m.sourceCreatedAt ?? null,
            caption: m.caption || m.text,
            createdAt: m.createdAt,
          } as ApiMediaItem)),
          total: listResult.total,
        };
        setTotal(result.total);
      }

      const apiItems = result.items as ApiMediaItem[];
      setItems(apiItems);
      // Sync to MediaContext so TranscribeTool can see them
      for (const item of apiItems) {
        addContextItem(apiItemToContextItem(item));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load media');
    } finally {
      setLoading(false);
    }
  }, [mediaApi, page, typeFilter, sourceFilter, searchQuery]);

  useEffect(() => {
    fetchMedia();
  }, [fetchMedia]);

  // React to pendingMediaId from PanelContext (e.g. image click in workspace)
  useEffect(() => {
    const pendingId = panelState.pendingMediaId;
    if (!pendingId || !items.length) return;

    const item = items.find(i => i.id === pendingId);
    if (item) {
      // Select and scroll to the item
      setSelectedId(pendingId);
      onSelectMedia?.(item);
      setAssociations([]);

      // Fetch associations
      if (item.sourcePointer && window.electronArchive?.getMediaContext) {
        window.electronArchive.getMediaContext(item.sourcePointer).then(result => {
          const seen = new Set<string>();
          const unique = result.associations.filter(a => {
            if (!a.threadId || seen.has(a.threadId)) return false;
            seen.add(a.threadId);
            return true;
          });
          setAssociations(unique);
        }).catch(() => {});
      }

      // Scroll into view after a brief layout tick
      requestAnimationFrame(() => {
        const el = containerRef.current?.querySelector(`[data-media-id="${pendingId}"]`);
        el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });

      // Consume the pending ID
      setPendingMediaId(null);
    } else {
      // Item not on current page — reset filters and refetch from page 0
      setTypeFilter('all');
      setSourceFilters({});
      setSearchQuery('');
      setPage(0);
      // The ID will be matched on next items render after refetch
    }
  }, [panelState.pendingMediaId, items]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleItemClick = useCallback((item: ApiMediaItem, e: React.MouseEvent) => {
    const isMultiSelect = e.metaKey || e.ctrlKey;
    const isRangeSelect = e.shiftKey;

    if (isRangeSelect && lastClickedIdRef.current) {
      // Shift+click: range selection
      contextSelectRange(lastClickedIdRef.current, item.id);
    } else if (isMultiSelect) {
      // Cmd/Ctrl+click: toggle individual item in context selection
      contextToggleSelection(item.id);
    } else {
      // Plain click: single-select for detail panel + context
      contextDeselectAllSelection();
      contextSelectItem(item.id);
    }
    lastClickedIdRef.current = item.id;

    // Always update detail panel for the clicked item
    setSelectedId(item.id);
    setAssociations([]);
    onSelectMedia?.(item);

    // Fetch conversation associations for this media item (Electron only)
    if (item.sourcePointer && window.electronArchive?.getMediaContext) {
      window.electronArchive.getMediaContext(item.sourcePointer).then(result => {
        // Deduplicate by threadId (same conversation may have multiple references)
        const seen = new Set<string>();
        const unique = result.associations.filter(a => {
          if (!a.threadId || seen.has(a.threadId)) return false;
          seen.add(a.threadId);
          return true;
        });
        setAssociations(unique);
      }).catch((err) => {
        log.warn('Failed to get context for', item.sourcePointer, ':', err?.message ?? err);
      });
    }
  }, [onSelectMedia, contextSelectItem, contextDeselectAllSelection, contextToggleSelection, contextSelectRange]);

  const getMediaTypeFromMime = (mime: string): 'image' | 'audio' | 'video' | 'other' => {
    if (mime.startsWith('image/')) return 'image';
    if (mime.startsWith('audio/')) return 'audio';
    if (mime.startsWith('video/')) return 'video';
    return 'other';
  };

  const formatTooltip = (item: ApiMediaItem): string => {
    const parts: string[] = [];
    if (item.filename) parts.push(item.filename);
    if (item.sourceType) parts.push(`Source: ${item.sourceType}`);
    if (item.sourceCreatedAt) parts.push(`Created: ${new Date(item.sourceCreatedAt).toLocaleDateString()}`);
    else if (item.createdAt) parts.push(`Imported: ${new Date(item.createdAt).toLocaleDateString()}`);
    if (item.mimeType) parts.push(item.mimeType);
    if (item.width && item.height) parts.push(`${item.width}×${item.height}`);
    if (item.size) parts.push(`${(item.size / 1024).toFixed(0)} KB`);
    if (item.sourcePointer) parts.push(item.sourcePointer);
    if (item.caption) parts.push(item.caption.length > 80 ? item.caption.slice(0, 80) + '…' : item.caption);
    return parts.join('\n');
  };

  const handleCleanup = useCallback(async (dryRun: boolean) => {
    if (!window.electronArchive?.mediaCleanup) return;
    setCleanupRunning(true);
    setCleanupResult(null);
    try {
      const result = await window.electronArchive.mediaCleanup({ dryRun });
      setCleanupResult(result);
      // Refresh media list after actual cleanup
      if (!dryRun && result.deletedRows > 0) fetchMedia();
    } catch {
      setCleanupResult(null);
    } finally {
      setCleanupRunning(false);
    }
  }, [fetchMedia]);

  // Wrap transcription workspace callback to also trigger panel refresh
  const handleOpenTranscriptionInWorkspace = useCallback((text: string, modelLabel: string) => {
    onOpenTranscriptionInWorkspaceExternal?.(text, modelLabel);
    setTranscriptionRefreshKey((k) => k + 1);
  }, [onOpenTranscriptionInWorkspaceExternal]);

  // Show message when media API is not available
  if (!mediaApi.isAvailable) {
    return (
      <div className="media-browser">
        <div className="panel__empty">
          <div className="panel__empty-icon">🎬</div>
          <div className="panel__empty-title">Media Browser</div>
          <div className="panel__empty-description">
            {mediaApi.unavailableReason || 'Media browser not available.'}
            {mediaApi.backend !== 'none' && (
              <>
                <br />
                <span className="panel__empty-badge">{mediaApi.backend}</span>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="media-browser">
      {/* Controls */}
      <div className="media-browser__controls">
        <input
          className="media-browser__search"
          type="search"
          placeholder="Search media..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setPage(0);
          }}
        />

        <div className="media-browser__filters">
          {(['all', 'image', 'audio', 'video'] as MediaTypeFilter[]).map((t) => (
            <button
              key={t}
              className={`media-browser__filter-btn ${typeFilter === t ? 'media-browser__filter-btn--active' : ''}`}
              onClick={() => { setTypeFilter(t); setPage(0); }}
            >
              {t === 'all' ? 'All' : t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>

        {availableSources.length > 0 && (
          <SourceFilterPills
            sources={availableSources}
            filters={sourceFilters}
            onFiltersChange={(f) => { setSourceFilters(f); setPage(0); }}
            mode="radio"
            showIcons={false}
            className=""
          />
        )}

        <div className="media-browser__view-actions">
          <button
            className={`media-browser__view-btn ${viewMode === 'grid' ? 'media-browser__view-btn--active' : ''}`}
            onClick={() => setViewMode('grid')}
            title="Grid view"
          >
            ▦
          </button>
          <button
            className={`media-browser__view-btn ${viewMode === 'list' ? 'media-browser__view-btn--active' : ''}`}
            onClick={() => setViewMode('list')}
            title="List view"
          >
            ☰
          </button>
          {viewMode === 'grid' && (
            <input
              type="range"
              min={80}
              max={300}
              value={thumbSize}
              onChange={(e) => setThumbSize(Number(e.target.value))}
              className="media-browser__size-slider"
              aria-label="Thumbnail size"
            />
          )}
          {onExpandGallery && (
            <button
              className="media-browser__expand-btn"
              onClick={onExpandGallery}
              title="Expand to full workspace"
            >
              ⤢
            </button>
          )}
          {items.length > 0 && (
            <button
              className="media-browser__select-all-btn"
              onClick={() => {
                if (contextSelectedCount === items.length) {
                  contextDeselectAllSelection();
                } else {
                  for (const item of items) {
                    contextSelectItem(item.id);
                  }
                }
              }}
              title={contextSelectedCount === items.length ? 'Deselect all on page' : 'Select all on page'}
            >
              {contextSelectedCount === items.length ? 'Deselect All' : 'Select All'}
            </button>
          )}
          {isElectron && window.electronArchive?.mediaCleanup && (
            <button
              className="media-browser__cleanup-btn"
              onClick={() => handleCleanup(true)}
              disabled={cleanupRunning}
              title="Scan for orphaned media (missing files, broken references)"
            >
              {cleanupRunning ? 'Scanning...' : 'Cleanup'}
            </button>
          )}
        </div>
      </div>

      {/* Multi-select info bar */}
      {contextSelectedCount > 0 && (
        <div className="media-browser__selection-bar">
          <span className="media-browser__selection-count">
            {contextSelectedCount} selected
          </span>
          <button
            className="media-browser__deselect-btn"
            onClick={() => contextDeselectAllSelection()}
          >
            Clear selection
          </button>
        </div>
      )}

      {/* Cleanup results banner */}
      {cleanupResult && (
        <div className="media-browser__cleanup-result">
          <span>
            {cleanupResult.missingFiles === 0 && cleanupResult.orphanedRefs === 0
              ? 'No issues found.'
              : `${cleanupResult.missingFiles} missing file${cleanupResult.missingFiles !== 1 ? 's' : ''}, ${cleanupResult.orphanedRefs} orphaned ref${cleanupResult.orphanedRefs !== 1 ? 's' : ''}`}
            {cleanupResult.deletedRows > 0 && ` — ${cleanupResult.deletedRows} rows cleaned up.`}
          </span>
          {cleanupResult.dryRun && cleanupResult.missingFiles > 0 && (
            <button
              className="media-browser__cleanup-apply"
              onClick={() => handleCleanup(false)}
              disabled={cleanupRunning}
            >
              Clean up
            </button>
          )}
          <button
            className="media-browser__cleanup-dismiss"
            onClick={() => setCleanupResult(null)}
          >
            ×
          </button>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="media-browser__error">
          {error}
          <button className="media-browser__retry-btn" onClick={fetchMedia}>Retry</button>
        </div>
      )}

      {/* Loading */}
      {loading && items.length === 0 && (
        <div className="media-browser__loading">Loading media...</div>
      )}

      {/* Empty state */}
      {!loading && items.length === 0 && !error && (
        <div className="media-browser__empty">
          No media items found.
          {searchQuery && ' Try a different search.'}
        </div>
      )}

      {/* Media grid/list */}
      <div
        ref={containerRef}
        className={viewMode === 'list' ? 'media-browser__items media-browser__items--list' : 'media-browser__masonry'}
        style={viewMode === 'grid' ? { '--masonry-cols': colCount } as React.CSSProperties : undefined}
      >
        {items.map((item) => {
          const mediaType = getMediaTypeFromMime(item.mimeType);
          const fileId = extractFileId(item.sourcePointer);
          const dateLabel = formatMediaDate(item);
          const isContextSelected = contextSelectedIds.has(item.id);
          return (
            <button
              key={item.id}
              data-media-id={item.id}
              className={`media-thumb ${selectedId === item.id ? 'media-thumb--selected' : ''} ${isContextSelected ? 'media-thumb--multi-selected' : ''}`}
              onClick={(e) => handleItemClick(item, e)}
              title={formatTooltip(item)}
            >
              {isContextSelected && (
                <span className="media-thumb__check" aria-hidden="true">&#10003;</span>
              )}
              {mediaType === 'image' ? (
                <div className="media-thumb__image-container">
                  <img
                    className="media-thumb__image"
                    src={item.sourcePointer
                      ? resolveMediaUrl(item.sourcePointer)
                      : `/api/media/serve/${item.id}`}
                    alt={item.caption || item.filename || fileId || item.contentHash}
                    loading="lazy"
                    onError={(e) => {
                      log.warn('Image load failed:', item.sourcePointer ?? item.id);
                      const target = e.currentTarget;
                      target.style.display = 'none';
                      const container = target.parentElement;
                      if (!container) return;
                      container.classList.add('media-thumb__image-container--fallback');
                      // Build info card for broken images (use textContent to avoid XSS)
                      const info = document.createElement('div');
                      info.className = 'media-thumb__broken-info';
                      const typeSpan = document.createElement('span');
                      typeSpan.className = 'media-thumb__broken-type';
                      typeSpan.textContent = 'IMAGE';
                      info.appendChild(typeSpan);
                      if (fileId) {
                        const idSpan = document.createElement('span');
                        idSpan.className = 'media-thumb__broken-id';
                        idSpan.textContent = fileId;
                        info.appendChild(idSpan);
                      }
                      if (item.caption) {
                        const capSpan = document.createElement('span');
                        capSpan.className = 'media-thumb__broken-caption';
                        capSpan.textContent = item.caption.length > 60 ? item.caption.slice(0, 60) + '\u2026' : item.caption;
                        info.appendChild(capSpan);
                      }
                      if (dateLabel) {
                        const dateSpan = document.createElement('span');
                        dateSpan.className = 'media-thumb__broken-date';
                        dateSpan.textContent = dateLabel;
                        info.appendChild(dateSpan);
                      }
                      container.appendChild(info);
                    }}
                  />
                </div>
              ) : (
                <div className="media-thumb__icon">
                  {mediaType === 'audio' ? '🎵' : mediaType === 'video' ? '🎬' : '📄'}
                  {dateLabel && <span className="media-thumb__date">{dateLabel}</span>}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Associations for selected item */}
      {selectedId && (() => {
        const selectedItem = items.find(i => i.id === selectedId);
        if (!selectedItem) return null;
        return (
          <>
            {associations.length > 0 && (
              <div className="media-browser__associations">
                <div className="media-browser__associations-label">
                  Referenced in {associations.length} conversation{associations.length > 1 ? 's' : ''}:
                </div>
                {associations.map((assoc) => (
                  <button
                    key={assoc.threadId}
                    className="media-browser__association-link"
                    onClick={() => onOpenThread?.(assoc.threadId)}
                    title={assoc.snippet || undefined}
                  >
                    <span className="media-browser__association-title">
                      {assoc.conversationTitle || 'Untitled conversation'}
                    </span>
                    {assoc.conversationDate && (
                      <span className="media-browser__association-date">
                        {new Date(assoc.conversationDate).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
            <TranscriptionPanel
              mediaId={selectedItem.id}
              archiveId={LOCAL_ARCHIVE_UUID}
              onOpenInWorkspace={handleOpenTranscriptionInWorkspace}
              refreshKey={transcriptionRefreshKey}
              className="media-browser__transcriptions"
            />
          </>
        );
      })()}

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div className="media-browser__pagination">
          <button
            className="media-browser__page-btn"
            disabled={page === 0}
            onClick={() => setPage(p => p - 1)}
          >
            ← Prev
          </button>
          <span className="media-browser__page-info">
            {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
          </span>
          <button
            className="media-browser__page-btn"
            disabled={(page + 1) * PAGE_SIZE >= total}
            onClick={() => setPage(p => p + 1)}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}

export default MediaBrowser;
